import { cn } from '@/lib/utils';
import { SegmentChip } from '@/components/ui/segment-chip';
import type { ProspectAnalytics, ProspectFilters } from '../../types';

type Segment = NonNullable<ProspectFilters['segment']>;

interface SegmentBarProps {
  analytics: ProspectAnalytics;
  segment: Segment;
  onSegmentChange: (segment: Segment) => void;
  isLoading?: boolean;
  /** Cards per row. Cards wrap onto more rows instead of scrolling sideways. */
  columns?: 6 | 3 | 2;
}

const COLUMNS_CLASS: Record<NonNullable<SegmentBarProps['columns']>, string> = {
  6: 'grid-cols-6',
  3: 'grid-cols-3',
  2: 'grid-cols-2'
};

function compactMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value || 0);
}

function compactNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

export function SegmentBar({ analytics, segment, onSegmentChange, isLoading, columns = 6 }: SegmentBarProps) {
  const placeholder = isLoading ? '—' : null;

  const chips: Array<{
    key: Segment;
    label: string;
    value: string;
    sub?: string;
    accent: 'primary' | 'ok' | 'danger' | 'warn' | 'info' | 'accent';
    informational?: boolean;
    title?: string;
  }> = [
    {
      key: 'all',
      label: 'All active',
      value: placeholder ?? compactNumber(analytics.totalProspects),
      sub: placeholder ?? `${compactMoney(analytics.totalLoanVolume)} total`,
      accent: 'primary'
    },
    {
      key: 'locked',
      label: 'Locked',
      value: placeholder ?? compactNumber(analytics.lockedCount),
      sub: placeholder ?? `${compactMoney(analytics.lockedVolume)} volume`,
      accent: 'ok'
    },
    {
      key: 'expiring',
      label: 'Lock expiring',
      value: placeholder ?? compactNumber(analytics.lockExpiringCount),
      sub: 'Within 5 days',
      accent: 'danger'
    },
    {
      key: 'at_risk',
      label: 'At risk',
      value: placeholder ?? compactNumber(analytics.atRiskCount),
      sub: 'Lock risk or stalled',
      accent: 'warn'
    },
    {
      key: 'funded',
      label: 'Funded MTD',
      value: placeholder ?? compactNumber(analytics.fundedMtdCount),
      sub: placeholder ?? `${compactMoney(analytics.fundedMtdVolume)} funded`,
      accent: 'info'
    },
    {
      key: 'avg',
      label: 'Avg amount',
      value: placeholder ?? compactMoney(analytics.averageLoanAmount),
      sub: placeholder ?? `${compactMoney(analytics.totalLoanVolume)} total`,
      accent: 'accent',
      informational: true,
      title: 'Average loan amount. Informational only — does not filter the list.'
    }
  ];

  return (
    <div
      className={cn('grid gap-1.5 rounded-lg border bg-background p-1.5', COLUMNS_CLASS[columns])}
      // A labelled group of toggle buttons (aria-pressed), not a tablist:
      // the chips filter one list rather than switch between panels.
      role="group"
      aria-label="Pipeline segments"
    >
      {chips.map(chip => (
        <SegmentChip
          key={chip.key}
          label={chip.label}
          value={chip.value}
          sub={chip.sub}
          active={segment === chip.key}
          accent={chip.accent}
          onClick={() => onSegmentChange(chip.key)}
          informational={chip.informational}
          title={chip.title}
        />
      ))}
    </div>
  );
}
